import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Types
import { ShowOTProps } from './types';
import { variant } from '../../SpecialTag/types';
import { OrderState, OrderType } from '../../../../types/orders';
import { useAuth } from '../../../../hooks/useAuth';
import { Role } from '../../../../types/auth';
import { canRoleChangeStateOf } from '../../../../utils/rolePermissions';
// Styles Components
import {
  ContainerContent,
  ContainerDescription,
  ContainerHeader,
  ContainerOT,
  InputDescription,
  InputPKEnd,
  InputPKInit,
  PkInputsContainer,
  StyledSpecialTag,
} from './styles';

// Components
import { SpecialTag } from '../../SpecialTag';
import { TagActionsMenu } from '../../SpecialTag/TagActionsMenu';

// Hooks
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';

// Redux
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../modals/SuperModal/types';
import { TitleOrder, TagsContainer } from '../MainStyledComponents';

export const SimpleWorkOrder = ({
  name,
  pkInit,
  pkEnd,
  state,
  indexExpedition,
  revalidatorFunction,
  validation,
  registerEndDate,
  orderId,
  orderDescription,
  actionDescription,
  routeRoid,
  routeId,
  linkedSegmentRoid,
}: ShowOTProps): JSX.Element => {
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLandscapeTablet = useIsLandscapeTablet();
  const { currentRole } = useAuth();
  /**
   * ¿Hay algún estado al que llevar esta actuación? Antes esto preguntaba
   * `canRoleChangeEntityState(rol, Action)`, que solo dice si el rol toca el
   * TIPO — y para el Tablet dice que sí (las crea), aunque no pueda moverla a
   * ninguna parte: el chip se pintaba pulsable y el modal salía vacío.
   *
   * Se retira además la condición `parentOrderState === Open`, que no tenía
   * equivalente ni en el backend ni en la otra tarjeta que pinta lo mismo
   * (`ContentCardResume`): con el padre en cualquier otro estado el clic se
   * TRAGABA en silencio — ni modal, ni aviso, ni nada. Un Coex en una
   * actuación de OT en curso pulsaba el chip y no pasaba absolutamente nada.
   */
  const canChangeState = canRoleChangeStateOf(
    currentRole as Role,
    OrderType.Action,
    // `state` llega tipado como `variant` (el enum de colores del chip), que
    // comparte los valores con `OrderState` pero no el tipo.
    state as unknown as OrderState
  );
  const openModalChangeState = () => {
    canChangeState &&
      dispatch(
        openModal(modalIds.CHANGE_STATE_MODAL, {
          title: t('workOrder.changeStateConfirm'),
          titleSaveModal:
            'Estàs segur que vols tancar tot el que estigui associat a aquesta actuació?',
          indexExpedition,
          revalidatorFunction,
          orderType: OrderType.Action,
          registerEndDate,
          currentState: state,
          orderId: orderId,
        })
      );
  };
  return (
    <ContainerOT>
      <ContainerHeader>
        <TitleOrder>{name}</TitleOrder>
        {/* Las etiquetas van JUNTAS a la derecha, con el contenedor compartido
            de las cards de ShowOT: mismo gap, mismo wrap y misma alineación a
            la derecha en tablet. Así el chip del recorrido queda pegado al de
            estado, como «Transferido» o «Vincular a recorrido» en una OT
            (feedback usuario 2026-08-05). */}
        <TagsContainer>
          {validation &&
            (canChangeState ? (
              <StyledSpecialTag
                variant={state as variant}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
                onClick={openModalChangeState}
              />
            ) : (
              // Sin destinos posibles el estado se sigue VIENDO, pero no
              // finge ser pulsable (`StyledSpecialTag` es el mismo chip con
              // `cursor: pointer`).
              <SpecialTag
                variant={state as variant}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
              />
            ))}
          {/* Actuació bàsica: de dónde viene y qué tramo resolvió. Sin esto se
              abre huérfana desde la tabla de validación y nada dice que salió
              de una patrulla. Dos relaciones distintas: el chip del recorrido
              NAVEGA (garnet, como los demás chips que te llevan a un sitio) y
              el del tramo es informativo (verde, la MISMA pill que se ve en la
              card de esta actuación dentro del recorrido). */}
          {!!routeRoid &&
            (routeId ? (
              <TagActionsMenu
                variant={variant.FromRoute}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
                title={routeRoid}
                actions={[
                  {
                    icon: 'view',
                    label: t('common:buttons.view'),
                    onClick: () => navigate(`/surveillance-route/${routeId}`),
                  },
                ]}
              />
            ) : (
              // Sin id de entidad no hay a dónde navegar (la ruta pide el id
              // del SurveillanceRoute, no el de su Order gemela): tag plano.
              <SpecialTag
                variant={variant.FromRoute}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
                title={routeRoid}
              />
            ))}
          {!!linkedSegmentRoid &&
            (routeId ? (
              // El tramo se ve DENTRO de su recorrido (no tiene pantalla
              // propia), así que este chip lleva al mismo sitio que el de
              // arriba: el detalle del recorrido, donde está la tabla de
              // tramos. Conserva su color verde — cambia lo que hace, no lo
              // que significa.
              <TagActionsMenu
                variant={variant.LinkedToSegment}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
                title={t('pages:surveillance.linkedWith', {
                  roid: linkedSegmentRoid,
                })}
                actions={[
                  {
                    icon: 'view',
                    label: t('common:buttons.view'),
                    onClick: () => navigate(`/surveillance-route/${routeId}`),
                  },
                ]}
              />
            ) : (
              // Sin id de entidad del recorrido no hay a dónde ir: tag plano.
              <SpecialTag
                variant={variant.LinkedToSegment}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
                title={t('pages:surveillance.linkedWith', {
                  roid: linkedSegmentRoid,
                })}
              />
            ))}
        </TagsContainer>
      </ContainerHeader>

      <ContainerContent>
        <PkInputsContainer>
          <InputPKInit
            label={t('workOrder.pkInitial')}
            typeInput="normal"
            disabled={true}
            defaultValue={pkInit}
          />
          <InputPKEnd
            label={t('workOrder.pkFinal')}
            typeInput="normal"
            disabled={true}
            defaultValue={pkEnd}
          />
        </PkInputsContainer>
        <ContainerDescription>
          {!!orderDescription?.trim() && (
            <InputDescription
              label={t('workOrder.description')}
              disabled={true}
              defaultValue={orderDescription}
            />
          )}
          {/* Igual que orderDescription: si no hay descripción, no pintamos
              el campo vacío — la única entrada de texto del wizard es
              "Descripción validada" en Datos Act (descriptionValidated ||
              description es la descripción EFECTIVA en cards/resumen/PDF). */}
          {!!actionDescription?.trim() && (
            <InputDescription
              label={t('action.actionDescription')}
              disabled={true}
              defaultValue={actionDescription}
            />
          )}
        </ContainerDescription>
      </ContainerContent>
    </ContainerOT>
  );
};
